// src/hooks/usePresence.js
// ─────────────────────────────────────────────────────────────────────────────
// Supabase Realtime presence — broadcasts who is online in the same org.
// Each user broadcasts: { userId, name, initials, color, page, activeEvent }
// Other users receive the full presence state and render avatar clusters.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "../config/supabase";

// Deterministic color per user so avatars are stable across sessions
const AVATAR_COLORS = [
  "#0891B2", "#0E7490", "#6366F1", "#7C3AED",
  "#059669", "#D97706", "#DC2626", "#DB2777",
];
function colorForId(id) {
  if (!id) return AVATAR_COLORS[0];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initialsFor(name, email) {
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/);
    return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
  }
  if (email) return email.slice(0, 2).toUpperCase();
  return "??";
}

export function usePresence({ orgId, userId, userName, userEmail, currentPage, activeEvent }) {
  const channelRef = useRef(null);
  const [online, setOnline] = useState([]); // array of presence state objects (excluding self)

  const broadcast = useCallback(() => {
    if (!channelRef.current || !userId) return;
    channelRef.current.track({
      userId,
      name:        userName || userEmail?.split("@")[0] || "Unknown",
      initials:    initialsFor(userName, userEmail),
      color:       colorForId(userId),
      page:        currentPage || "",
      activeEvent: activeEvent || "",
      ts:          Date.now(),
    });
  }, [userId, userName, userEmail, currentPage, activeEvent]);

  useEffect(() => {
    if (!orgId || !userId) return;

    // One presence channel per org — scoped so users only see teammates
    const channel = supabase.channel(`presence:${orgId}`, {
      config: { presence: { key: userId } },
    });

    channel
      .on("presence", { event: "sync" }, () => {
        const state = channel.presenceState();
        const others = Object.entries(state)
          .filter(([key]) => key !== userId)
          .map(([, presences]) => presences[0]) // latest from each user
          .filter(Boolean)
          .sort((a, b) => a.name.localeCompare(b.name));
        setOnline(others);
      })
      .subscribe(async (status) => {
        if (status === "SUBSCRIBED") {
          await channel.track({
            userId,
            name:        userName || userEmail?.split("@")[0] || "Unknown",
            initials:    initialsFor(userName, userEmail),
            color:       colorForId(userId),
            page:        currentPage || "",
            activeEvent: activeEvent || "",
            ts:          Date.now(),
          });
        }
      });

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [orgId, userId]); // only reconnect on org/user change

  // Re-broadcast on page/event change without reconnecting
  useEffect(() => {
    broadcast();
  }, [broadcast]);

  return { online };
}
